import { Button } from "@/components/ui";
import { AlertDialog } from "@/components/ui";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/Dropdown";
import {
  type AvailableActionsResponse_Action,
  DistributionAction,
  UnavailableReason,
  useAvailableActions,
} from "@/network/useAvailableActions";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useEncounter } from "@/network/useEncounter";
import { useParams, useSearch } from "@tanstack/react-router";
import { useAtom } from "jotai";
import {
  BanIcon,
  CirclePause,
  CircleSlashIcon,
  DoorClosed,
  EllipsisVertical,
  Hand,
  MessagesSquareIcon,
  NotebookPenIcon,
  PillBottleIcon,
  Users,
} from "lucide-react";
import { useState } from "react";
import { AmazonCarePlanPreviewModal } from "../CarePlanPreview/AmazonCarePlanPreviewModal";
import { UniversalClinicCarePlanPreviewModal } from "../CarePlanPreview/UniversalClinicCarePlanPreviewModal";
import { amazonCarePlanPreviewAtom } from "../atoms/amazonCarePlanPreview";
import { asyncRxCarePlanPreviewAtom } from "../atoms/asyncRxCarePlanPreview";
import { AdminNoteModal } from "../components/AdminNote/AdminNoteModal";
import { useVisitClosureDialog } from "../components/VisitClosure/visitClosureDialogContext";
import { RequiresLiveVisitRejectionModal } from "./RequiresLiveVisitRejectionModal";
import { type MenuItem, RoomOptionsMenu } from "./RoomOptionsMenu";
import { TakePatientButton } from "./TakePatientButton";
import {
  type CloseEncounterParams,
  WaitingRoomType,
} from "./network/useEncounterTransitions";
import { useVisitCancelReasonCodes } from "./network/useVisitCancelReasonCodes";
import { useRoomOptions } from "./useRoomOptions";

const ASYNC_ROOM_OPTION_TOOLTIPS = {
  [UnavailableReason.PRESCRIPTIONS_ALREADY_SENT]:
    "Prescriptions have already been sent for this visit.",
  [UnavailableReason.NO_CAREPLAN_WITH_EOC]:
    "A CarePlan has not been created for this visit.",
  [UnavailableReason.NO_SENT_PRESCRIPTIONS]:
    "No prescriptions have been sent for this visit.",
  [UnavailableReason.LIVE_CHAT_IS_OPEN]:
    "Cannot close when a live chat is open.",
  [UnavailableReason.MISSING_VISIT_DX]:
    "A diagnosis has not been added to this visit.",
};

export const RoomOptions = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const searchParams = useSearch({ from: "/patient/$patientId" });
  const { trigger: triggerVisitClosureDialog } = useVisitClosureDialog();
  const { data: isSingleEncounterVisitsEnabled } = useConfigCatFeatureFlag(
    "single-encounter-visits",
  );
  const encounterId = searchParams.encounterId as string;
  const { data: encounter, isLoading: isEncounterLoading } = useEncounter(
    encounterId,
    patientId,
  );
  const isAmazon = Boolean(encounter?.amazonId);

  const { data: availableActions } = useAvailableActions({
    patientId,
    encounterId: encounterId,
  });

  const [amazonCarePlanPreview, setAmazonCarePlanPreview] = useAtom(
    amazonCarePlanPreviewAtom,
  );

  const [asyncRxCarePlanPreview, setAsyncRxCarePlanPreview] = useAtom(
    asyncRxCarePlanPreviewAtom,
  );

  const { data: reasonCodes } = useVisitCancelReasonCodes();

  const [adminNoteOpen, setAdminNoteOpen] = useState(false);
  const [rxRenewalCarePlanPreviewOpen, setRxRenewalCarePlanPreviewOpen] =
    useState(false);
  const [asyncDxCarePlanPreviewOpen, setAsyncDxCarePlanPreviewOpen] =
    useState(false);
  const [showHandoffPrompt, setShowHandoffPrompt] = useState(false);
  const [showPausePrompt, setShowPausePrompt] = useState(false);
  const [requiresLiveVisitPreviewOpen, setRequiresLiveVisitPreviewOpen] =
    useState(false);

  const {
    actions,
    pending,
    isDropdownOpen,
    setIsDropdownOpen,
    isEncounterClosable,
  } = useRoomOptions({
    encounterId,
    isAmazon,
    patientId,
  });

  const handleSendAsyncRxCareplan = async () => {
    setAsyncRxCarePlanPreview((prev) => ({ ...prev, open: false }));
    await actions.complete({ incomplete: false });
  };

  const handleSendRxRenewalCareplan = async () => {
    setRxRenewalCarePlanPreviewOpen(false);
    await actions.complete({ incomplete: false });
  };

  const handleSendAsyncDxCareplan = async () => {
    setAsyncDxCarePlanPreviewOpen(false);
    await actions.complete({ incomplete: false });
  };

  const handleCloseEncounter = async (
    { incomplete }: CloseEncounterParams,
    closeMutation: (params: CloseEncounterParams) => void,
  ) => {
    if (!isAmazon && !(await isEncounterClosable())) {
      return;
    }
    if (isAmazon && amazonCarePlanPreview.carePlanId) {
      setAmazonCarePlanPreview((prev) => ({
        ...prev,
        open: true,
      }));
      return;
    }
    if (availableActions?.BILLING?.allowed) {
      triggerVisitClosureDialog({
        mode: "billing",
        onSuccess: (programEnrollment) =>
          closeMutation({ incomplete, programEnrollment }),
        visitCompleted: !incomplete,
      });
    } else {
      await closeMutation({ incomplete: incomplete });
    }
  };

  const handleCloseAsyncRxVisit = () => {
    if (isSingleEncounterVisitsEnabled === true) {
      triggerVisitClosureDialog({
        mode: "billingless",
        carePlanIds: asyncRxCarePlanPreview.carePlanIds,
        onSendCarePlan: handleSendAsyncRxCareplan,
      });
      return;
    }

    // Open the care plan preview modal
    setAsyncRxCarePlanPreview((prev) => ({
      ...prev,
      open: true,
    }));
  };

  const handleCloseRxRenewal = () => {
    if (isSingleEncounterVisitsEnabled === true) {
      triggerVisitClosureDialog({
        mode: "billingless",
        carePlanIds: asyncRxCarePlanPreview.carePlanIds,
        onSendCarePlan: handleSendRxRenewalCareplan,
      });
      return;
    }

    setRxRenewalCarePlanPreviewOpen(true);
  };

  const handleCloseAsyncDx = () => {
    if (isSingleEncounterVisitsEnabled === true) {
      triggerVisitClosureDialog({
        mode: "billingless",
        carePlanIds: asyncRxCarePlanPreview.carePlanIds,
        onSendCarePlan: handleSendAsyncDxCareplan,
      });
      return;
    }

    // Open the care plan preview modal
    setAsyncDxCarePlanPreviewOpen(true);
  };

  const handleRequiresLiveVisit = () => {
    if (!isSingleEncounterVisitsEnabled) {
      setRequiresLiveVisitPreviewOpen(true);
      return;
    }

    triggerVisitClosureDialog({
      mode: "rejectAsyncVisit",
      onRejectAsyncVisit: actions.rejectAsyncRequest,
    });
  };

  const roomOptionsOrder = [
    DistributionAction.PAUSE_PENDING_PT,
    DistributionAction.COMPLETE,
    DistributionAction.STEAL,
    DistributionAction.CANCEL,
    DistributionAction.INCOMPLETE,
    DistributionAction.HANDOFF_TO_CA,
    DistributionAction.HANDOFF_TO_LC,
    DistributionAction.CLOSE_ASYNC_RX_VISIT,
    DistributionAction.CLOSE_RX_RENEWAL_VISIT,
    DistributionAction.CLOSE_ASYNC_DX,
    DistributionAction.REQUIRES_LIVE_VISIT,
    DistributionAction.ADD_ADMIN_NOTE,
    DistributionAction.START_CLINICAL_NOTE,
  ] as const;

  type DropdownAction = (typeof roomOptionsOrder)[number];

  const roomOptionsDropdownConfig: Record<
    DropdownAction,
    Omit<MenuItem, "key">
  > = {
    [DistributionAction.ADD_ADMIN_NOTE]: {
      icon: NotebookPenIcon,
      label: "Add Admin Note",
      condition: Boolean(availableActions?.ADD_ADMIN_NOTE?.allowed),
      onClick: () => setAdminNoteOpen(true),
      pending: adminNoteOpen,
    },
    [DistributionAction.CANCEL]: {
      icon: BanIcon,
      label: "Cancel Visit",
      condition: Boolean(availableActions?.CANCEL?.allowed),
      children: reasonCodes?.visitCancelReasonCodes.map((r) => ({
        key: `cancel-${r.code}`,
        label: r.display,
        onClick: () =>
          actions.cancel({
            cancelReasonCode: r.code,
            cancelReasonDisplay: r.display,
          }),
      })),
      pending: pending.cancel,
    },
    [DistributionAction.COMPLETE]: {
      icon: DoorClosed,
      label: "Close Visit",
      condition: Boolean(availableActions?.COMPLETE?.allowed),
      onClick: () =>
        handleCloseEncounter({ incomplete: false }, actions.complete),
      pending: pending.complete,
    },
    [DistributionAction.HANDOFF_TO_CA]: {
      icon: Users,
      label: "Handoff to CA",
      condition: Boolean(availableActions?.HANDOFF_TO_CA?.allowed),
      onClick: () => actions.handoffCA({ waitingRoomType: WaitingRoomType.CA }),
      pending: pending.handoffCA,
    },
    [DistributionAction.HANDOFF_TO_LC]: {
      icon: Users,
      label: "Handoff to Licensed Clinician",
      condition: Boolean(availableActions?.HANDOFF_TO_LC?.allowed),
      onClick: () => setShowHandoffPrompt(true),
      pending: pending.handoffLC,
    },
    [DistributionAction.INCOMPLETE]: {
      icon: CircleSlashIcon,
      label: "Incomplete Visit",
      condition: Boolean(availableActions?.INCOMPLETE?.allowed),
      onClick: () =>
        handleCloseEncounter({ incomplete: true }, actions.incomplete),
      pending: pending.incomplete,
    },
    [DistributionAction.PAUSE_PENDING_PT]: {
      icon: CirclePause,
      label: "Pending Patient Response",
      condition: Boolean(availableActions?.PAUSE_PENDING_PT?.allowed),
      onClick: () => setShowPausePrompt(true),
      pending: pending.pause,
    },
    [DistributionAction.CLOSE_ASYNC_RX_VISIT]: {
      icon: PillBottleIcon,
      label: "Close Rx Request",
      condition: Boolean(availableActions?.CLOSE_ASYNC_RX?.allowed),
      disabledTooltip:
        ASYNC_ROOM_OPTION_TOOLTIPS[
          availableActions?.CLOSE_ASYNC_RX
            ?.notAllowedReason as UnavailableReason
        ] ?? null,
      pending: pending.complete,
      onClick: handleCloseAsyncRxVisit,
    },
    [DistributionAction.CLOSE_RX_RENEWAL_VISIT]: {
      icon: PillBottleIcon,
      label: "Close Rx Renewal",
      condition: Boolean(availableActions?.CLOSE_RX_RENEWAL?.allowed),
      disabledTooltip:
        ASYNC_ROOM_OPTION_TOOLTIPS[
          availableActions?.CLOSE_RX_RENEWAL
            ?.notAllowedReason as UnavailableReason
        ] ?? null,
      pending: pending.complete,
      onClick: handleCloseRxRenewal,
    },
    [DistributionAction.CLOSE_ASYNC_DX]: {
      icon: PillBottleIcon,
      label: "Close Async DX",
      condition: Boolean(availableActions?.CLOSE_ASYNC_DX?.allowed),
      disabledTooltip:
        ASYNC_ROOM_OPTION_TOOLTIPS[
          availableActions?.CLOSE_ASYNC_DX
            ?.notAllowedReason as UnavailableReason
        ] ?? null,
      pending: pending.complete,
      onClick: handleCloseAsyncDx,
    },
    [DistributionAction.REQUIRES_LIVE_VISIT]: {
      icon: MessagesSquareIcon,
      label: "Requires Live Visit",
      condition: Boolean(availableActions?.REQUIRES_LIVE_VISIT?.allowed),
      disabledTooltip:
        ASYNC_ROOM_OPTION_TOOLTIPS[
          availableActions?.REQUIRES_LIVE_VISIT
            ?.notAllowedReason as UnavailableReason
        ] ?? null,
      pending: pending.rejectAsyncRequest,
      onClick: handleRequiresLiveVisit,
    },
    [DistributionAction.START_CLINICAL_NOTE]: {
      icon: NotebookPenIcon,
      label: "Start Clinical Note",
      condition:
        Boolean(availableActions?.START_CLINICAL_NOTE?.allowed) && isAmazon,
      onClick: actions.clinicalNote,
      pending: pending.clinicalNote,
    },
    [DistributionAction.STEAL]: {
      icon: Hand,
      label: "Steal Encounter",
      condition: Boolean(availableActions?.STEAL?.allowed),
      onClick: actions.steal,
      pending: pending.steal,
    },
  };

  const allowedDropdownActions = new Set<DistributionAction>(roomOptionsOrder);
  const isAnyDropdownActionAvailable = Object.values(
    availableActions ?? {},
  ).some(
    (action: AvailableActionsResponse_Action) =>
      action?.allowed &&
      allowedDropdownActions.has(action.action as DistributionAction),
  );

  const dropdownItems: MenuItem[] = roomOptionsOrder.map((action) => ({
    key: action,
    ...roomOptionsDropdownConfig[action],
  }));

  const handleConfirmHandoff = async () => {
    setShowHandoffPrompt(false);
    await actions.handoffLC({
      waitingRoomType: WaitingRoomType.CLINICIAN,
    });
  };

  const handleConfirmPause = async () => {
    setShowPausePrompt(false);
    await actions.pause();
  };

  if (isEncounterLoading) {
    return null;
  }

  const handleSendAmazonCareplan = async () => {
    await actions.complete({ incomplete: false });
  };

  const handleSendRequiresLiveVisit = async ({
    cancelReasonCode,
    cancelReasonText,
  }: {
    cancelReasonCode: string;
    cancelReasonText?: string;
  }) => {
    try {
      await actions.rejectAsyncRequest({
        cancelReasonCode,
        cancelReasonText,
      });
      setRequiresLiveVisitPreviewOpen(false);
    } catch {
      // Error toast is shown by the mutation's onError; keep the modal open
      // so the user can retry without re-entering their reason.
    }
  };

  return (
    <div className="flex gap-2 items-center">
      <AdminNoteModal
        open={adminNoteOpen}
        onOpenChange={setAdminNoteOpen}
        isAmazon={Boolean(isAmazon)}
      />
      {isAmazon && (
        <AmazonCarePlanPreviewModal
          onSendCareplan={handleSendAmazonCareplan}
          isCarePlanSending={pending.complete}
        />
      )}
      {asyncRxCarePlanPreview.carePlanIds.length > 0 && (
        <UniversalClinicCarePlanPreviewModal
          open={asyncRxCarePlanPreview.open}
          onOpenChange={(open) =>
            setAsyncRxCarePlanPreview((prev) => ({ ...prev, open }))
          }
          carePlanIds={asyncRxCarePlanPreview.carePlanIds}
          onSendCarePlan={handleSendAsyncRxCareplan}
          isSending={pending.complete}
          sendButtonLabel="Send & Close Visit"
        />
      )}
      <RequiresLiveVisitRejectionModal
        open={requiresLiveVisitPreviewOpen}
        onOpenChange={setRequiresLiveVisitPreviewOpen}
        onSubmit={handleSendRequiresLiveVisit}
        isSending={pending.rejectAsyncRequest}
      />
      {asyncRxCarePlanPreview.carePlanIds.length > 0 && (
        <UniversalClinicCarePlanPreviewModal
          open={rxRenewalCarePlanPreviewOpen}
          onOpenChange={setRxRenewalCarePlanPreviewOpen}
          carePlanIds={asyncRxCarePlanPreview.carePlanIds}
          onSendCarePlan={handleSendRxRenewalCareplan}
          isSending={pending.complete}
          sendButtonLabel="Send & Close Visit"
        />
      )}
      {asyncRxCarePlanPreview.carePlanIds.length > 0 && (
        <UniversalClinicCarePlanPreviewModal
          open={asyncDxCarePlanPreviewOpen}
          onOpenChange={setAsyncDxCarePlanPreviewOpen}
          carePlanIds={asyncRxCarePlanPreview.carePlanIds}
          onSendCarePlan={handleSendAsyncDxCareplan}
          isSending={pending.complete}
          sendButtonLabel="Send & Close Visit"
        />
      )}

      <TakePatientButton takeAction={availableActions?.TAKE} />

      {isAnyDropdownActionAvailable && (
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className="leading-tight px-0 h-6 py-0"
              size="sm"
              variant="icon"
              data-testid="room-options"
              isLoading={pending.anyAction}
            >
              <EllipsisVertical className="text-link" size={18} />
            </Button>
          </DropdownMenuTrigger>
          <RoomOptionsMenu
            items={dropdownItems}
            anyActionPending={pending.anyAction}
          />
        </DropdownMenu>
      )}

      <AlertDialog
        isOpen={showHandoffPrompt}
        onOpenChange={setShowHandoffPrompt}
        isControlled
        title="Handoff to Licensed Clinician"
        body="Are you sure you want to hand off this patient to a Licensed Clinician?"
        actionLabel="Handoff"
        cancelLabel="Cancel"
        onAction={handleConfirmHandoff}
        onCancel={() => setShowHandoffPrompt(false)}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
      <AlertDialog
        isOpen={showPausePrompt}
        onOpenChange={setShowPausePrompt}
        isControlled
        title="Pause visit"
        body="Are you sure you want to pause this visit?"
        actionLabel="Pause"
        cancelLabel="Cancel"
        onAction={handleConfirmPause}
        onCancel={() => setShowPausePrompt(false)}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </div>
  );
};
